import { Button } from '@mui/material';
import type { ButtonProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';

type AppButtonVariant = 'primary' | 'secondary' | 'outlined';

interface AppButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: AppButtonVariant;
}

export const AppButton = ({
  variant = 'primary',
  children,
  disabled = false,
  sx,
  ...props
}: AppButtonProps) => {
  const theme = useTheme();

  const getVariantConfig = () => {
    const baseConfig = {
      textTransform: 'none' as const,
      fontWeight: 500,
      '&:disabled': {
        backgroundColor: theme.palette.action.disabled,
        color: theme.palette.action.disabledText
      }
    };

    switch (variant) {
      case 'primary':
        return {
          variant: 'contained' as const,
          color: 'primary' as const,
          sx: baseConfig
        };
      case 'secondary':
        return {
          variant: 'contained' as const,
          sx: {
            ...baseConfig,
            backgroundColor: theme.palette.grey[800],
            color: theme.palette.background.paper,
            '&:hover': {
              backgroundColor: theme.palette.grey[700] || theme.palette.grey[800]
            }
          }
        };
      case 'outlined':
        return {
          variant: 'outlined' as const,
          color: 'primary' as const,
          sx: baseConfig
        };
      default:
        return {
          variant: 'contained' as const,
          color: 'primary' as const,
          sx: baseConfig
        };
    }
  };

  const config = getVariantConfig();

  return (
    <Button
      {...config}
      disabled={disabled}
      sx={{ ...config.sx, ...sx }}
      {...props}
    >
      {children}
    </Button>
  );
};
